import React, { useMemo, useState } from 'react';

import useDepartmentStore from '../../stores/departmentStore';
import { useScope } from '../../context/PermissionContext';
import { SPECIALIZATION_LABELS, STAFF_ROLES } from '../../constants/staff';
import {
  ADMISSION_ACCESS_ERROR_CODE,
  ADMISSION_ACCESS_ERROR_COPY,
  outOfDepartmentCopy,
  doctorDisplayName,
  ADD_DOCTOR_HELPER_COPY,
  ADD_DOCTOR_ROW_PRIMARY_LABEL,
  ADD_DOCTOR_ROW_ADDED_LABEL,
  ADD_DOCTOR_SEARCH_PLACEHOLDER,
  ADD_DOCTOR_PARTIAL_FAILED_COPY,
  addDoctorFailedCopy,
  addDoctorsTitle,
} from '../../constants/admission';
import { addTreatingDoctor } from '../../services/api/admissionApi';
import { invalidateStaffRoster } from '../../services/api/staffApi';
import { useDoctorRoster } from './hooks';

/**
 * AddTreatingDoctorDialog — "+ Add doctor" on the admission Details
 * (admission-access C-6, mockup 5-treating-doctors). The picker is ORG-WIDE by
 * owner ruling (no department wall: added doctors are usually consultants from
 * other departments); the department select is a convenience filter only.
 * Rows already on the team stay in the list, DISABLED and labelled (copy-
 * ratified #36); inactive staff never reach it (`mapDoctorsResponse`).
 *
 * Multi-select: the server adds ONE doctor per call, so the picks go out one
 * after the other (never in parallel — every add bumps the row's
 * `care_team_rev` CAS), stopping at the first failure. The caller's
 * `onChanged` reloads the admission once at the end, and ALWAYS before a
 * conflict message is shown (the message says the list is now up to date).
 *
 * Runtime safety: the roster comes from `useDoctorRoster` (one fetch per
 * mount, single-flight + cached in staffApi); nothing here fetches on render.
 */

// ── Copy ── ratified (#35, #36, #45, #46, #55, #56) — single source in
// constants/admission.js; re-exported here for the existing imports.
export {
  ADD_DOCTOR_HELPER_COPY,
  ADD_DOCTOR_ROW_PRIMARY_LABEL as ROW_PRIMARY_LABEL,
  ADD_DOCTOR_ROW_ADDED_LABEL as ROW_ADDED_LABEL,
  ADD_DOCTOR_SEARCH_PLACEHOLDER,
  ADD_DOCTOR_PARTIAL_FAILED_COPY,
} from '../../constants/admission';

const PAGE = 8;
const PAGE_MORE = 10;

/**
 * Codes whose cause is a stale view of the admission: the caller reloads it
 * before the message is shown (CARE_TEAM_CONFLICT: someone else changed the
 * doctors; ALREADY_PRIMARY: the primary changed under us; CARE_TEAM_LIMIT: the
 * list we hold is shorter than the server's; ADMISSION_TERMINAL: the summary
 * locked; CARE_TEAM_NOT_PERMITTED: the viewer's own role on the admission changed).
 */
export const CARE_TEAM_RELOAD_CODES = new Set([
  ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_CONFLICT,
  ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_LIMIT,
  ADMISSION_ACCESS_ERROR_CODE.ALREADY_PRIMARY,
  ADMISSION_ACCESS_ERROR_CODE.ADMISSION_TERMINAL,
  ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_NOT_PERMITTED,
]);

/** Code → ratified copy; the wire `message` is never rendered. */
export const careTeamErrorCopy = (res, fallback, role) => (
  res && res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE
    ? outOfDepartmentCopy(role)
    : (ADMISSION_ACCESS_ERROR_COPY[res && res.code] || fallback)
);

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-[560px] max-h-[90vh] flex flex-col';
const inputCls = 'px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';

export default function AddTreatingDoctorDialog({ admission, onClose, onChanged }) {
  const doctors = useDoctorRoster();
  const { role, memberId } = useScope();
  // A doctor who is not on the team (the duty doctor) cannot add HIMSELF: hidden from his own picker.
  const selfHiddenId = role === STAFF_ROLES.DOCTOR && admission.viewer_relationship == null ? memberId : null;
  const departments = useDepartmentStore((s) => s.departments);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  // Department text only once the member→department edges are loaded — before
  // that "No department" would be a false claim about every doctor.
  const deptsReady = useDepartmentStore((s) => s._allMemberDeptsHydrated);
  const [q, setQ] = useState('');
  const [deptId, setDeptId] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState(() => new Set());
  // Doctors the server called invalid (deactivated since the roster loaded):
  // dropped from this open list, the cached roster is dropped for the next open.
  const [gone, setGone] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const primaryId = admission.primary_doctor_member_id || null;
  const addedDoctors = admission.added_doctors;
  const offLabels = useMemo(() => {
    const m = {};
    if (primaryId) m[primaryId] = ADD_DOCTOR_ROW_PRIMARY_LABEL;
    (addedDoctors || []).forEach((d) => { m[d.member_id] = ADD_DOCTOR_ROW_ADDED_LABEL; });
    return m;
  }, [primaryId, addedDoctors]);

  const deptOptions = useMemo(
    () => (departments || []).filter((d) => d.active !== false).map((d) => ({ id: d.dept_id || d.id, name: d.name })),
    [departments],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matched = doctors
      .filter((d) => !gone.has(d.member_id) && d.member_id !== selfHiddenId)
      .map((d) => ({
        ...d,
        label: doctorDisplayName(d.name || d.display_name),
        spec: d.specialization ? (SPECIALIZATION_LABELS[d.specialization] || d.specialization) : '',
        deptEdges: deptsByMember[d.member_id] || [],
      }))
      .filter((d) => !deptId || d.deptEdges.some((e) => e.dept_id === deptId))
      .filter((d) => !needle
        || `${d.label} ${d.spec} ${d.deptEdges.map((e) => e.name).join(' ')}`.toLowerCase().includes(needle));
    // Pickable first, the already-on-the-team rows (disabled) after them.
    return matched.filter((d) => !offLabels[d.member_id]).concat(matched.filter((d) => offLabels[d.member_id]));
  }, [doctors, deptsByMember, q, deptId, gone, offLabels, selfHiddenId]);

  const toggle = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    if (busy || selected.size === 0) return;
    setBusy(true);
    setError(null);
    const picked = Array.from(selected);
    let addedCount = 0;
    let failure = null;
    for (const memberId of picked) {
      // One at a time on purpose: concurrent adds would race the row's CAS.
      const res = await addTreatingDoctor(admission.admission_id, memberId);
      if (!res.success) { failure = { res, memberId }; break; }
      addedCount += 1;
    }
    // The caller reloads the admission: once after a clean run, and (before the
    // message) whenever the failure says our view was stale or something was added.
    const staleView = failure && CARE_TEAM_RELOAD_CODES.has(failure.res.code);
    if ((!failure || staleView || addedCount > 0) && typeof onChanged === 'function') await onChanged();
    if (!failure) {
      setBusy(false);
      onClose();
      return;
    }
    if (failure.res.code === ADMISSION_ACCESS_ERROR_CODE.DOCTOR_INVALID) {
      invalidateStaffRoster();
      setGone((cur) => new Set(cur).add(failure.memberId));
    }
    // Picks that went through are on the team now: leave only the rest selected.
    setSelected((cur) => {
      const next = new Set(cur);
      picked.slice(0, addedCount).forEach((id) => next.delete(id));
      if (failure.res.code === ADMISSION_ACCESS_ERROR_CODE.DOCTOR_INVALID) next.delete(failure.memberId);
      return next;
    });
    setError(careTeamErrorCopy(failure.res, addedCount > 0 ? ADD_DOCTOR_PARTIAL_FAILED_COPY : addDoctorFailedCopy(picked.length), role));
    setBusy(false);
  };

  const n = selected.size;
  const visible = rows.slice(0, shown);
  const hidden = rows.length - visible.length;
  return (
    <div className={overlayCls} data-testid="add-treating-doctor-dialog">
      <div className={panelCls} role="dialog" aria-label="Add doctors">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{addDoctorsTitle(admission.patient_name)}</h3>
          <button type="button" onClick={onClose} disabled={busy} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 overflow-y-auto">
          <div className="mb-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12px] text-slate-600 dark:text-slate-300 p-2.5" data-testid="add-doctor-helper">
            {ADD_DOCTOR_HELPER_COPY}
          </div>
          <div className="flex gap-2 mb-2.5">
            <input
              type="text"
              className={`${inputCls} flex-1`}
              placeholder={ADD_DOCTOR_SEARCH_PLACEHOLDER}
              aria-label="Search doctors"
              value={q}
              onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
              autoFocus
              data-testid="add-doctor-search"
            />
            <select
              className={inputCls}
              aria-label="Department"
              value={deptId}
              onChange={(e) => { setDeptId(e.target.value); setShown(PAGE); }}
              data-testid="add-doctor-dept"
            >
              <option value="">All departments</option>
              {deptOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 max-h-[320px] overflow-y-auto" data-testid="add-doctor-list">
            {visible.length === 0 && (
              <div className="p-3 text-[12.5px] text-slate-500 dark:text-slate-400">
                {q.trim() ? `No doctor matches “${q.trim()}”.` : 'No doctors to show.'}
              </div>
            )}
            {visible.map((d) => {
              const off = offLabels[d.member_id];
              const deptText = !deptsReady ? '' : (d.deptEdges.length ? d.deptEdges.map((e) => e.name).join(', ') : 'No department');
              const sub = [d.spec, deptText].filter(Boolean).join(' · ');
              return (
                <label
                  key={d.member_id}
                  className={`flex items-center gap-2.5 px-3 py-2 border-b last:border-b-0 border-slate-100 dark:border-slate-700 ${off ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40'}`}
                  data-testid={`add-doctor-row-${d.member_id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(d.member_id)}
                    disabled={!!off || busy}
                    onChange={() => toggle(d.member_id)}
                    aria-label={d.label}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-slate-800 dark:text-slate-100 truncate">{d.label}</span>
                    <span className="block text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{sub}</span>
                  </span>
                  {off && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 whitespace-nowrap">{off}</span>
                  )}
                </label>
              );
            })}
          </div>
          {hidden > 0 && (
            <div className="flex items-center gap-2 mt-2 text-[12px] text-slate-500 dark:text-slate-400" data-testid="add-doctor-more">
              <span className="flex-1">+{hidden} more {hidden === 1 ? 'doctor' : 'doctors'} — type a name to narrow</span>
              <button type="button" className={btnSecondary} onClick={() => setShown((s) => s + PAGE_MORE)}>
                Show {Math.min(PAGE_MORE, hidden)} more
              </button>
            </div>
          )}
          {rows.length > 0 && (
            <div className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">Showing {visible.length} of {rows.length}</div>
          )}
          {error && (
            <div role="alert" className="mt-2.5 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="add-doctor-error">
              {error}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <span className="flex-1 text-[12.5px] text-slate-500 dark:text-slate-400">
            {n > 0
              ? (
                <>
                  <b className="text-teal-700 dark:text-teal-300">{n} selected</b>{' '}
                  <button type="button" className="underline" onClick={() => setSelected(new Set())} disabled={busy}>Clear</button>
                </>
              )
              : 'Pick one or more doctors'}
          </span>
          <button type="button" className={btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={btnPrimary}
            onClick={submit}
            disabled={n === 0 || busy}
            title={n === 0 ? 'Pick at least one doctor' : undefined}
            data-testid="add-doctor-confirm"
          >
            {busy ? 'Adding…' : (n > 1 ? `Add ${n} doctors` : 'Add doctor')}
          </button>
        </div>
      </div>
    </div>
  );
}
